import { createContext, useContext, useEffect, useState } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      // Skip the /auth/me probe when we have no stored token — avoids
      // noisy 401s on public pages (landing, about, login, forgot-password).
      if (!localStorage.getItem("rv_token")) {
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (error) {
        // Token invalid/expired — clear it so we don't retry.
        localStorage.removeItem("rv_token");
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password, remember = false) => {
    // Defensive: clear any stale token BEFORE the POST so the request
    // interceptor can't attach an old Authorization header to the login call.
    // A stale header itself never fails /auth/login (it's a public endpoint),
    // but belt-and-suspenders to avoid any weird proxy/preflight edge case.
    localStorage.removeItem("rv_token");
    const { data } = await api.post("/auth/login", { email, password, remember });
    if (data.token) localStorage.setItem("rv_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    if (data.token) localStorage.setItem("rv_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (error) { console.warn("Logout API call failed; clearing local state anyway", error); }
    localStorage.removeItem("rv_token");
    setUser(null);
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (error) {
      console.warn("Session refresh failed", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
